import React, { useState } from "react";
import { FaTimesCircle } from "react-icons/fa";
import axiosInstance from "../../utils/axiosConfig";
import { TailSpin } from "react-loader-spinner";
import { toast } from "react-hot-toast";
import { API_URL } from "../../constant";
import { formatAmount } from "../../utils/FormatPerson";

const formatOtcDate = (dateString: string | undefined) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const day = date.getDate();
  const ordinalSuffix = (d: number) => {
    if ([1, 21, 31].includes(d)) return "st";
    if ([2, 22].includes(d)) return "nd";
    if ([3, 23].includes(d)) return "rd";
    return "th";
  };
  return `${date.toLocaleString("default", {
    month: "short",
  })} ${day}${ordinalSuffix(day)}, ${date.getFullYear()}, ${date.toLocaleString(
    "default",
    { hour: "numeric", minute: "numeric", hour12: true }
  )}`;
};

const getStatusConfig = (status: string) => {
  const s = (status || "").toUpperCase();
  if (s === "PENDING")
    return {
      label: "Pending",
      copy: "Waiting to be processed",
      class: "bg-[#2D2400] text-[#CB9200] border-[#CB9200]/30",
    };
  if (s === "PROCESSING")
    return {
      label: "Processing",
      copy: "Awaiting your action to complete",
      class: "bg-[#0D1F2D] text-[#4FB0FF] border-[#4FB0FF]/30",
    };
  if (s === "CONFIRMED")
    return {
      label: "Confirmed",
      copy: "Deposit verified on-chain",
      class: "bg-[#0E2B1F] text-[#0EA5E9] border-[#0EA5E9]/30",
    };
  if (s === "COMPLETED")
    return {
      label: "Completed",
      copy: "Payment sent successfully",
      class: "bg-[#0E2B1F] text-[#03C668] border-[#03C668]/30",
    };
  return {
    label: status || "—",
    copy: "",
    class: "bg-[#1C2227] text-[#869AA9] border-[#27313A]",
  };
};

type OtcDetailModalProps = {
  transaction: any;
  onClose: () => void;
  onCompleteSuccess?: () => void;
};

const OtcDetailModal = ({
  transaction,
  onClose,
  onCompleteSuccess,
}: OtcDetailModalProps) => {
  const [completeLoading, setCompleteLoading] = useState(false);

  if (!transaction) return null;

  const isDeposit = transaction.kind === "DEPOSIT";
  const isPayout = transaction.kind === "PAYOUT";
  const showCompleteButton =
    isPayout &&
    transaction.status === "PROCESSING" &&
    typeof onCompleteSuccess === "function";

  const statusConfig = getStatusConfig(transaction.status);

  const completeManualPayment = () => {
    if (!transaction._id || completeLoading) return;
    setCompleteLoading(true);
    axiosInstance
      .patch(
        `${API_URL}api/admin/otc/payouts/${transaction._id}/complete-manual`,
        { payoutId: transaction._id }
      )
      .then(() => {
        toast.success("Manual payment completed");
        onCompleteSuccess?.();
        setTimeout(() => {
          onClose();
        }, 1500);
      })
      .catch((e: any) => {
        const msg = e.response?.data?.message || "Failed to complete payment";
        toast.error(msg);
      })
      .finally(() => setCompleteLoading(false));
  };

  const Row = ({
    label,
    value,
    mono,
  }: {
    label: string;
    value: React.ReactNode;
    mono?: boolean;
  }) => (
    <div className="w-full py-3.5 flex flex-col xs:flex-row xs:justify-between xs:items-center gap-1.5 border-b border-[#1F2A32] last:border-0">
      <span className="text-[#6F7D89] text-[12px] sm:text-[13px] font-medium uppercase tracking-wide shrink-0">
        {label}
      </span>
      <span
        className={`text-[13px] sm:text-[15px] font-semibold text-[#E7EBEE] text-right break-all ${
          mono ? "font-mono text-[11px] sm:text-[12px]" : ""
        }`}
      >
        {value ?? "—"}
      </span>
    </div>
  );

  const Section = ({
    title,
    copy,
    children,
    accent = "default",
  }: {
    title: string;
    copy?: string;
    children: React.ReactNode;
    accent?: "default" | "green" | "blue";
  }) => {
    const borderClass =
      accent === "green"
        ? "border-l-[#03C668]/50"
        : accent === "blue"
        ? "border-l-[#4FB0FF]/50"
        : "border-l-[#27313A]";
    return (
      <div
        className={`rounded-xl border border-[#1F2A32] bg-[#0D1115]/90 overflow-hidden ${borderClass} border-l-4`}
      >
        <div className="px-4 sm:px-5 py-4 sm:py-4 border-b border-[#1F2A32]">
          <h3 className="text-[#E7EBEE] font-bold text-[15px] sm:text-[16px]">
            {title}
          </h3>
          {copy && (
            <p className="text-[#6F7D89] text-[12px] sm:text-[13px] mt-1">
              {copy}
            </p>
          )}
        </div>
        <div className="px-4 sm:px-5 py-3 sm:py-4">{children}</div>
      </div>
    );
  };

  const primaryAmount =
    isDeposit && transaction.amountCrypto != null
      ? `${formatAmount(transaction.amountCrypto)} ${
          transaction.currency || ""
        }`
      : isPayout && transaction.payoutAmountNGN != null
      ? `₦${formatAmount(transaction.payoutAmountNGN)}`
      : isPayout && transaction.asset?.amountInUSD != null
      ? `$${formatAmount(transaction.asset.amountInUSD)}`
      : null;

  return (
    <div className="fixed inset-0 flex font-manrope justify-center items-center py-4 sm:py-6 px-3 sm:px-4 bg-black/70 backdrop-blur-sm z-[100] overflow-y-auto">
      <div className="w-full max-w-[520px] lg:max-w-[560px] h-[85vh] min-h-[420px] max-h-[95vh] flex flex-col transition-all p-5 sm:p-7 rounded-2xl bg-gradient-to-b from-[#0C1116] to-[#0D1115] border border-[#1F2A32] shadow-[0_24px_80px_rgba(0,0,0,0.5)] my-auto">
        {/* Header */}
        <div className="flex justify-between items-start gap-4 mb-5 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`inline-block px-2.5 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider ${
                  isDeposit
                    ? "bg-[#0D1F2D] text-[#4FB0FF]"
                    : "bg-[#1A2E1A] text-[#5CD198]"
                }`}
              >
                {isDeposit ? "Deposit" : "Payout"}
              </span>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold border ${statusConfig.class}`}
              >
                {statusConfig.label}
              </span>
            </div>
            <h2 className="text-[22px] sm:text-[26px] font-bold text-white mt-2.5 font-sora">
              OTC {isDeposit ? "Deposit" : "Payout"} Details
            </h2>
            <p className="text-[#6F7D89] text-[13px] sm:text-[14px] mt-1.5">
              {isDeposit
                ? "Crypto received from user — review below."
                : "Fiat sent to user's bank — review below."}
            </p>
            {statusConfig.copy && (
              <p className="text-[#869AA9] text-[11px] sm:text-[12px] mt-1.5 italic">
                {statusConfig.copy}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2.5 rounded-xl text-[#869AA9] hover:text-white hover:bg-[#1F2A32] transition shrink-0 touch-manipulation"
            aria-label="Close"
          >
            <FaTimesCircle className="text-[28px] sm:text-[32px]" />
          </button>
        </div>

        {/* Hero amount */}
        {primaryAmount && (
          <div className="rounded-xl bg-[#11181E] border border-[#1F2A32] px-5 sm:px-6 py-5 sm:py-6 mb-5 text-center shrink-0">
            <p className="text-[#6F7D89] text-[12px] uppercase tracking-wider mb-1.5">
              {isDeposit ? "Amount received" : "Amount sent"}
            </p>
            <p className="text-[30px] sm:text-[36px] font-bold text-[#BEE1FB] font-sora tracking-tight">
              {primaryAmount}
            </p>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-[#3A4852] scrollbar-track-[#13181B] hover:scrollbar-thumb-[#4F5F6B] space-y-5 pr-2 pb-1">
          <Section
            title="Transaction overview"
            copy="IDs and timestamps for this record"
          >
            <Row label="Transaction ID" value={transaction._id} mono />
            <Row label="User ID" value={transaction.userId} mono />
            <Row
              label="Date"
              value={formatOtcDate(
                transaction.processedAt || transaction.createdAt
              )}
            />
          </Section>

          {isDeposit && (
            <Section
              title="Deposit details"
              copy="Crypto received on-chain"
              accent="blue"
            >
              <Row label="Currency" value={transaction.currency} />
              <Row label="Network" value={transaction.network} />
              <Row
                label="Destination address"
                value={transaction.address}
                mono
              />
              <Row
                label="Txid / Hash"
                value={transaction.txid || transaction.hash}
                mono
              />
              <Row
                label="Amount (crypto)"
                value={
                  transaction.amountCrypto != null
                    ? `${formatAmount(transaction.amountCrypto)} ${
                        transaction.currency || ""
                      }`
                    : "—"
                }
              />
              <Row
                label="Amount (NGN)"
                value={
                  transaction.amountNGN != null
                    ? `₦${formatAmount(transaction.amountNGN)}`
                    : "—"
                }
              />
              <Row
                label="Amount (USD)"
                value={
                  transaction.amountUSD != null
                    ? `$${formatAmount(transaction.amountUSD)}`
                    : "—"
                }
              />
              <Row label="Provider" value={transaction.provider?.name} />
              <Row label="Wallet ID" value={transaction.walletId} mono />
              <Row
                label="Last updated"
                value={formatOtcDate(transaction.updatedAt)}
              />
            </Section>
          )}

          {isPayout && (
            <>
              <Section
                title="Payout details"
                copy="What we're sending and how"
                accent="green"
              >
                <Row label="Type" value={transaction.type} />
                <Row
                  label="Linked deposit ID"
                  value={transaction.depositId}
                  mono
                />
                <Row
                  label="Payout amount (NGN)"
                  value={
                    transaction.payoutAmountNGN != null
                      ? `₦${formatAmount(transaction.payoutAmountNGN)}`
                      : "—"
                  }
                />
                {transaction.asset && (
                  <>
                    <Row label="Asset" value={transaction.asset.name} />
                    <Row
                      label="Asset amount"
                      value={
                        transaction.asset.amountCrypto != null
                          ? formatAmount(transaction.asset.amountCrypto)
                          : "—"
                      }
                    />
                    <Row
                      label="Amount (USD)"
                      value={
                        transaction.asset.amountInUSD != null
                          ? `$${formatAmount(transaction.asset.amountInUSD)}`
                          : "—"
                      }
                    />
                  </>
                )}
              </Section>

              {transaction.bank && (
                <Section title="Bank account" copy="Where we're sending funds">
                  <Row
                    label="Account name"
                    value={transaction.bank.accountName}
                  />
                  <Row
                    label="Account number"
                    value={transaction.bank.accountNumber}
                  />
                  <Row label="Bank code" value={transaction.bank.bankCode} />
                </Section>
              )}

              {transaction.payment && (
                <Section title="Payment provider" copy="Settlement reference">
                  <Row label="Provider" value={transaction.payment.provider} />
                  <Row
                    label="Reference"
                    value={transaction.payment.reference}
                    mono
                  />
                  <Row
                    label="Batch ID"
                    value={transaction.payment.batchId}
                    mono
                  />
                </Section>
              )}

              <Section title="Timeline" copy="Key dates for this payout">
                <Row
                  label="Requested"
                  value={formatOtcDate(transaction.requestedAt)}
                />
                <Row
                  label="Processed"
                  value={formatOtcDate(transaction.processedAt)}
                />
                <Row
                  label="Completed"
                  value={formatOtcDate(transaction.completedAt)}
                />
              </Section>

              {transaction.logs?.length > 0 && (
                <Section title="Activity log" copy="Step-by-step history">
                  <div className="space-y-3">
                    {transaction.logs.map((log: any, i: number) => (
                      <div
                        key={log._id || i}
                        className="flex gap-3 text-[12px] sm:text-[13px] border-l-2 border-[#27313A] pl-3 py-2"
                      >
                        <span className="text-[#6F7D89] shrink-0">
                          {formatOtcDate(log.time)}
                        </span>
                        <span className="text-[#AEBCC6]">{log.comment}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </>
          )}
        </div>

        {showCompleteButton && (
          <div className="mt-5 pt-5 border-t border-[#1F2A32] shrink-0">
            <p className="text-[#6F7D89] text-[12px] sm:text-[13px] mb-4">
              Mark this payout as completed after you've sent the funds.
            </p>
            <button
              type="button"
              onClick={completeManualPayment}
              disabled={completeLoading}
              className="w-full h-[52px] sm:h-[56px] rounded-xl bg-[#0B84CB] hover:bg-[#0a73b0] text-white font-semibold text-[15px] flex justify-center items-center gap-2 disabled:opacity-50 transition shadow-lg shadow-[#0B84CB]/25 touch-manipulation"
            >
              {completeLoading ? (
                <TailSpin color="#fff" height={24} width={24} />
              ) : (
                "Complete manual payment"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OtcDetailModal;
